// The single source for the /features page: every section, every card, the
// detail each card opens, the limits table and the FAQs. Written for a club
// admin reading it: what stops being their problem, not what the code does.
//
// Card shape:
//   key     stable slug (also the ?feature= query value and the JSON-LD name)
//   title   the one-line claim on the card
//   blurb   one sentence under it
//   detail  bullets shown in the dialog (omit when `to` is a page that says it all)
//   to      a page the card opens instead of a dialog (formats pages)
//   more    an optional {to, label} link at the foot of the dialog
//   tag     'soon' | 'app'
//
// Keep it honest: every line here is live on web and app unless tagged. The
// commission number is the real one (StripeSettings.PlatformCommissionPercent).

export const COMMISSION_PERCENT = 2;

export const FEATURE_TAGS = {soon: 'Coming soon', app: 'In the app'};

export const FEATURE_LIMITS = [
  {label: 'Members', value: 'Unlimited'},
  {label: 'Admins', value: 'Unlimited'},
  {label: 'Competitions', value: 'Unlimited'},
  {label: 'Divisions', value: 'Unlimited'},
  {label: 'Players per division', value: 'Unlimited'},
  {label: 'Seasons', value: 'Unlimited'},
  {label: 'Entrants per event', value: 'Unlimited'},
  {label: 'Sports', value: 'All of them'},
  {label: 'Monthly fee', value: '£0'},
];

export const FEATURE_SECTIONS = [
  {
    key: 'setup',
    short: 'Setup',
    icon: 'mdi-rocket-launch-outline',
    title: 'Setting up a club',
    lead: 'Create the club, import members, share a join link.',
    items: [
      {
        key: 'create-club',
        demo: 'clubPreview',
        icon: 'mdi-home-plus-outline',
        title: 'Create a club',
        blurb: 'Name, location, who can join and a logo. A setup checklist shows what is left.',
        detail: [
          'Address autocomplete and a map pin, so members can find you.',
          'Add your logo and the club page is ready to share.',
          'A setup checklist on the admin dashboard shows what is left to do.',
        ],
      },
      {
        key: 'import-members',
        icon: 'mdi-file-upload-outline',
        title: 'Import members from a spreadsheet',
        blurb: 'Upload a CSV or spreadsheet. Columns are matched automatically and duplicates flagged.',
        detail: [
          'Any CSV or spreadsheet. Names, emails, phone numbers, whatever you keep.',
          'Fix or skip rows inline before anything is created.',
          'Send invites as part of the import, or hold them until you are ready.',
          'A report of exactly what was added, skipped and merged.',
        ],
      },
      {
        key: 'no-email-members',
        icon: 'mdi-account-plus-outline',
        title: 'Members without an email address',
        blurb: 'Add a player by name. They can claim the profile later with a link.',
        detail: [
          'Admin-managed players appear in every league, ladder and draw like anyone else.',
          'When they are ready, a claim link turns the profile into their own account with all their results.',
          'Mark juniors as you add them so the junior protections apply from day one.',
        ],
      },
      {
        key: 'join-links',
        icon: 'mdi-link-variant',
        title: 'Join links and QR posters',
        blurb: 'One managed link per club and competition, with a printable QR poster.',
        detail: [
          'Every club and every competition has its own managed link and a printable QR poster.',
          'Open to anyone, or club members only. Instant, or each request approved by you.',
          'Add a password, a usage limit, start and end dates and a welcome message.',
          'Show the link on your public page, hide it, or show it to members only.',
          'Regenerate it at any time and the old one stops working.',
        ],
      },
      {
        key: 'waiting-area',
        demo: 'placement',
        icon: 'mdi-account-clock-outline',
        title: 'Waiting area for new joiners',
        blurb: 'Hold joiners until an admin places them in a box, position or team.',
        detail: [
          'Suggested groupings for placing a full box of newcomers.',
          'People waiting can name the partners they would like to play with.',
          'Everyone is told when they have been placed.',
        ],
      },
    ],
  },
  {
    key: 'leagues',
    short: 'Box leagues',
    icon: 'mdi-table',
    title: 'Box leagues',
    lead: 'Divisions of any size, results entered by players, rollover to the next cycle.',
    items: [
      {
        key: 'box-leagues',
        icon: 'mdi-table',
        title: 'Box leagues',
        blurb: 'Divisions of similar strength. Everyone plays everyone, then moves up or down.',
        to: '/formats/box-leagues',
      },
      {
        key: 'rollover',
        icon: 'mdi-swap-vertical-bold',
        title: 'Rollover to the next cycle',
        blurb: 'Promotion, relegation, dropouts and new joiners handled in one step.',
        detail: [
          'Everyone is listed in finishing order across all divisions.',
          'Choose how many move up and down. Adjust box sizes to fit who is left.',
          'Drag anyone to a different box before you confirm.',
          'Handicaps and settings carry over, and the old season stays viewable.',
        ],
      },
      {
        key: 'self-service-results',
        demo: 'fixture',
        icon: 'mdi-scoreboard',
        title: 'Results entered by players',
        blurb: 'The opponent confirms, or results go straight in. Standings update immediately.',
        detail: [
          'Enter the score game by game or just the result, from the court.',
          'Opponent approval, admin approval or auto-approve, per competition.',
          'Bonus points for playing, winning or anything else you decide.',
          'A results grid and a standings table, live on web and app.',
        ],
      },
      {
        key: 'league-print',
        demo: 'leagueMatrix',
        icon: 'mdi-printer-outline',
        title: 'Printable tables',
        blurb: 'A PDF of the standings or the results grid.',
        detail: [
          'Standings table or results matrix, per division or the whole cycle.',
          'Preview before you print.',
          'Juniors appear as first name and initial unless a parent has given permission.',
        ],
      },
    ],
  },
  {
    key: 'ladders',
    short: 'Ladders',
    icon: 'mdi-ladder',
    title: 'Ladders',
    lead: 'Challenge rules, expiry, movement and inactivity are all settings.',
    items: [
      {
        key: 'ladders',
        icon: 'mdi-ladder',
        title: 'Challenge ladders',
        blurb: 'Challenge players above you to move up. Runs continuously.',
        to: '/formats/ladders',
      },
      {
        key: 'ladder-rules',
        demo: 'ladderRules',
        icon: 'mdi-tune-variant',
        title: 'Challenge rules',
        blurb: 'How far up or down a challenge can reach, how many can be open, what a decline costs.',
        detail: [
          'Challenge reach up and down, with separate rules for the top of the ladder.',
          'One challenge at a time, one sent plus any received, or no limit.',
          'A number of unrestricted challenges for players finding their level.',
          'Declining does nothing, drops the decliner, or counts as a forfeit.',
          'Several teams per player on a doubles ladder.',
        ],
      },
      {
        key: 'ladder-expiry',
        icon: 'mdi-timer-sand',
        title: 'Challenge expiry',
        blurb: 'Challenges expire after a set number of days, with warnings at 24 hours and 1 hour.',
        detail: [
          'Set how many days a challenge stays open.',
          'Both players are warned before it expires.',
          'If a team leaves the ladder, its open challenges are cancelled and everyone is told.',
        ],
      },
      {
        key: 'ladder-inactivity',
        demo: 'ladderRules',
        icon: 'mdi-trending-down',
        title: 'Inactivity demotion',
        blurb: 'Players who have not played for a set number of days drop a set number of places, after a warning.',
        detail: [
          'Optional, per ladder. Choose the number of quiet days and how far they drop.',
          'A warning email goes out before anything moves.',
          'Freeze a player who is injured or away.',
        ],
      },
      {
        key: 'ladder-movement',
        demo: 'ladder',
        icon: 'mdi-swap-horizontal',
        title: 'Win movement',
        blurb: 'The winner moves above the loser, or the two swap places.',
        detail: [
          'Winner goes above the loser: the classic ladder.',
          'Swap places: gentler movement for a social ladder.',
        ],
      },
      {
        key: 'ladder-ai',
        icon: 'mdi-crystal-ball',
        title: 'Challenge preview',
        blurb: 'An estimated chance of success for a challenge, based on both players\' results.',
        detail: [
          'Optional per ladder. Never shown for juniors.',
          'Takes handicaps into account where they are on.',
        ],
      },
    ],
  },
  {
    key: 'scheduling',
    short: 'Scheduling',
    icon: 'mdi-calendar-clock',
    title: 'Scheduling matches',
    lead: 'One side proposes a time and court, the other confirms. Reminders are automatic.',
    items: [
      {
        key: 'challenge-scheduling',
        icon: 'mdi-sword-cross',
        title: 'Challenge with a time and court',
        blurb: 'A suggested date, time and court go with the challenge. Accept, decline or counter-propose.',
        detail: [
          'A suggested date, time and court go with the challenge.',
          'Accept as suggested, or accept with a different time.',
          'The challenger or an admin can change the suggestion later.',
          'Admins can create a challenge on anyone\'s behalf.',
        ],
      },
      {
        key: 'match-scheduling',
        demo: 'whenAndWhere',
        icon: 'mdi-calendar-check-outline',
        title: 'Schedule any match',
        blurb: 'One side proposes a date, time and playing area. The other side confirms.',
        detail: [
          'Pick a date, time and playing area from the match card.',
          'The other side gets a notification and confirms or counters.',
          'Admins can set or clear a time for anyone.',
        ],
      },
      {
        key: 'match-reminders',
        icon: 'mdi-bell-ring-outline',
        title: 'Match reminders',
        blurb: '24 hours and 2 hours before a match, and a reminder to enter the result afterwards.',
        detail: [
          'Match reminders at 24 hours and 2 hours.',
          'A reminder when a match has been talked about but not booked.',
          'A result reminder once the time has passed.',
        ],
      },
      {
        key: 'calendar',
        icon: 'mdi-calendar-plus',
        title: 'Phone calendar',
        blurb: 'Add a match or challenge to the phone calendar from the app.',
        tag: 'app',
        detail: [
          'Challenges and scheduled matches go straight into the phone\'s calendar.',
        ],
      },
    ],
  },
  {
    key: 'tournaments',
    short: 'Tournaments',
    icon: 'mdi-trophy',
    title: 'Tournaments',
    lead: 'Knockout, or groups then knockout. Seeds, byes and third-place playoff.',
    items: [
      {
        key: 'tournaments',
        icon: 'mdi-trophy',
        title: 'Tournaments',
        blurb: 'Knockout, or groups then knockout with a set number going through.',
        to: '/formats/tournaments',
      },
      {
        key: 'draw',
        demo: 'groups',
        icon: 'mdi-tournament',
        title: 'Draw generation',
        blurb: 'Seeding by hand, at random or by group finish. Byes and third-place playoff.',
        detail: [
          'Generate, regenerate or clear the draw at any point before play.',
          'Byes assigned for you, or by hand.',
          'Progress from the groups to the knockout with one tap.',
          'Optional third-place playoff.',
        ],
      },
      {
        key: 'bracket',
        demo: 'bracket',
        icon: 'mdi-family-tree',
        title: 'Bracket view',
        blurb: 'Zoom and pan on the web, swipe round by round in the app.',
        detail: [
          'Group tables and the bracket on one public page.',
          'A champion banner once the final is approved.',
        ],
      },
      {
        key: 'tournament-entry',
        icon: 'mdi-account-multiple-plus-outline',
        title: 'Entry by link',
        blurb: 'Players enter via the join link until the draw is made.',
        detail: [
          'Entry fees can be collected as people enter.',
          'Team handicaps for doubles tournaments.',
        ],
      },
    ],
  },
  {
    key: 'americano',
    short: 'Americano',
    icon: 'mdi-account-switch',
    title: 'Americano leagues',
    lead: 'Individual entry, rotating partners, personal points.',
    items: [
      {
        key: 'americano',
        icon: 'mdi-account-switch',
        title: 'Americano leagues',
        blurb: 'Groups of 4, 8 or 12. Pairings generated. Promotion and relegation between rounds.',
        to: '/formats/americano-leagues',
      },
      {
        key: 'americano-rotation',
        demo: 'americanoRounds',
        icon: 'mdi-rotate-3d-variant',
        title: 'Pairings generated',
        blurb: 'Each player partners and faces every other player in the group.',
        detail: [
          'Whist rotation per division, so nobody sits out and nobody plays the same partner twice.',
          'Change the groups and the pairings are rebuilt.',
          'Swap in a sub mid-season without losing anyone\'s points.',
        ],
      },
      {
        key: 'americano-standings',
        demo: 'americano',
        icon: 'mdi-podium',
        title: 'Personal standings',
        blurb: 'Points count for the individual. Ties broken on sets, then games.',
        detail: [
          'Standings per division, with promotion and relegation at the end of each cycle.',
          'A per-player preview of who moves where before you confirm the next round.',
          'A PDF of standings and rounds for the noticeboard.',
        ],
      },
    ],
  },
  {
    key: 'meets',
    short: 'Club nights',
    icon: 'mdi-handshake',
    title: 'Club nights and meets',
    lead: 'Recurring or one-off sessions with RSVP, a waitlist and matches generated on the day.',
    items: [
      {
        key: 'meets',
        icon: 'mdi-handshake',
        title: 'Meets',
        blurb: 'A session members join once and RSVP to per date.',
        to: '/formats/meets',
      },
      {
        key: 'rsvp',
        demo: 'meet',
        icon: 'mdi-calendar-multiple-check',
        title: 'RSVP, waitlist and numbers',
        blurb: 'Minimum and maximum attendees, a waitlist, and reminders to people who have not answered.',
        detail: [
          'When a date is full, the next person to RSVP goes on the waitlist. When someone drops out, the next in line is offered the place automatically.',
          'Choose when RSVPs open: immediately, a number of days before, or at a set moment for first come first served.',
          'RSVPs close before the start so you can plan.',
          'A last-call reminder to anyone who has not answered while spots remain.',
          'If a date is under its minimum, you are alerted or it cancels itself.',
        ],
      },
      {
        key: 'meet-matches',
        demo: 'meetMatches',
        icon: 'mdi-shuffle-variant',
        title: 'Match generation',
        blurb: 'Matches made from attendees, at random or balanced by an ability order.',
        detail: [
          'Generate when you choose, when RSVPs close, or as soon as the date fills.',
          'Keep an ability order for the group and matches are balanced from it.',
          'Preview and edit the pairings before anyone sees them. Add a match on the day.',
          'Print the night\'s matches.',
        ],
      },
      {
        key: 'meet-day',
        demo: 'attendees',
        icon: 'mdi-clipboard-check-outline',
        title: 'Register',
        blurb: 'Walk-ins, no-shows, check-in and fee waivers on the day.',
        detail: [
          'Add a walk-in, mark a no-show, waive a fee, remove someone.',
          'Cancel, restore or move a date and everyone is told.',
          'Run a session without recording results if you just want the numbers.',
        ],
      },
    ],
  },
  {
    key: 'payments',
    short: 'Payments',
    icon: 'mdi-credit-card-outline',
    title: 'Payments',
    lead: 'Payments go to the club\'s own Stripe account. The only cost is 2% plus Stripe\'s fees, on payments taken through Tap Leagues.',
    items: [
      {
        key: 'own-account',
        icon: 'mdi-bank-outline',
        title: 'Club\'s own Stripe account',
        blurb: 'Onboarding inside Tap Leagues. Payouts on a schedule, or on demand.',
        detail: [
          'The club holds its own Stripe account. Tap Leagues does not hold the money.',
          `${COMMISSION_PERCENT}% commission plus Stripe's fees, only on payments taken through Tap Leagues. No per-entry fee, no setup fee.`,
          'Balance, payouts and payout schedule on the club dashboard.',
        ],
      },
      {
        key: 'wallets',
        icon: 'mdi-contactless-payment',
        title: 'Apple Pay, Google Pay and card',
        blurb: 'Members save a card once and are charged after they play.',
        detail: [
          'Saved cards, a default card, one-tap payment.',
          'Members see exactly what will be charged and when.',
          'A failed card gets an "action needed" email and a retry.',
        ],
      },
      {
        key: 'session-fees',
        demo: 'tiers',
        icon: 'mdi-cash-clock',
        title: 'Session fees',
        blurb: 'Charged per session, weekly or monthly, with the club\'s own cancellation terms.',
        detail: [
          'Your own cancellation terms: charge a percentage inside a cut-off, free before it.',
          'Nobody is charged for a late cancellation if someone else fills the spot.',
          'Your own terms and conditions, accepted before the first paid booking and again if you change them. Sample terms to start from.',
          'Change the price with notice, and members reconfirm.',
          'A booking confirmation email with the price and the terms.',
        ],
      },
      {
        key: 'entry-fees',
        icon: 'mdi-ticket-confirmation-outline',
        title: 'Entry fees',
        blurb: 'A fee on any ladder, league, tournament or americano, agreed on joining.',
        detail: [
          'Set a fee per competition or per season.',
          'Charge existing members too, or only new entrants.',
          'An overdue list, with waive, assign and settle-in-cash options.',
        ],
      },
      {
        key: 'bill-to',
        icon: 'mdi-account-child-outline',
        title: 'Pay for someone else',
        blurb: 'Assign a member\'s fees to another member, such as a parent. They accept first.',
        detail: [
          'Works for guests and family members too.',
          'The payer accepts before anything is charged.',
        ],
      },
      {
        key: 'pay-link',
        icon: 'mdi-link-lock',
        title: 'Pay by link',
        blurb: 'A payment link that works without an account.',
        detail: [
          'For entry fees and outstanding charges.',
          'Saved cards work on the link too.',
        ],
      },
      {
        key: 'refunds-receipts',
        icon: 'mdi-receipt-text-outline',
        title: 'Refunds, receipts and statements',
        blurb: 'Full or partial refunds, a receipt for every payment, an Excel statement.',
        detail: [
          'Refund one line, a custom amount, or every charge for a date.',
          'PDF receipts and receipt emails for every payment.',
          'An Excel statement for any period, or for one competition.',
        ],
      },
    ],
  },
  {
    key: 'handicaps',
    short: 'Handicaps',
    icon: 'mdi-scale-balance',
    title: 'Handicaps',
    lead: 'Handicaps in the sport\'s own units, shown on every match.',
    items: [
      {
        key: 'handicap-schemes',
        demo: 'handicapChips',
        icon: 'mdi-scale-balance',
        title: 'Handicap schemes per sport',
        blurb: 'Points per game, shots, goals, frames or owe-and-receive, or custom units.',
        detail: [
          'Tennis fifteens, squash and badminton points, snooker frame points, darts start reduction, bowls shots, football goals, or your own units.',
          'Per ladder, league, tournament or americano.',
          'Show the values to members or keep them to admins.',
        ],
      },
      {
        key: 'handicap-start',
        demo: 'handicap',
        icon: 'mdi-flag-checkered',
        title: 'Start shown on every match',
        blurb: 'The resolved start for any pairing, on the match card and in live scoring.',
        detail: [
          'Worked out for any pairing, even before a match exists.',
          'Loaded into live scoring automatically.',
          'A calculator to see what any two players would start at.',
        ],
      },
      {
        key: 'handicap-adjust',
        demo: 'handicapHistory',
        icon: 'mdi-auto-fix',
        title: 'Automatic adjustment',
        blurb: 'Handicaps move after each result, stepped or proportional, or are set by hand.',
        detail: [
          'Stepped or proportional adjustment after each result.',
          'A cap on how far a handicap can move per match, and provisional matches for newcomers.',
          'Bulk set a whole competition, and a full history of every change.',
        ],
      },
    ],
  },
  {
    key: 'scoring',
    short: 'Scoring',
    icon: 'mdi-scoreboard-outline',
    title: 'Results and scoring',
    lead: 'Result entry, scoring systems per sport, live scoring, match events.',
    items: [
      {
        key: 'scoring-systems',
        icon: 'mdi-counter',
        title: 'Scoring systems',
        blurb: 'Around 60 presets across the sports, plus custom systems and per-competition overrides.',
        detail: [
          'Best-of sets and games, points per game, frames, legs, X01, ends. Whatever your sport uses.',
          'Clubs build custom systems from a preset, with a preview.',
          'Override the points and bonuses per competition.',
        ],
      },
      {
        key: 'live-scoring',
        demo: 'liveScore',
        icon: 'mdi-broadcast',
        title: 'Live scoring',
        blurb: 'Point-by-point scoring from the court, a spectator scoreboard, offline support.',
        detail: [
          'A scoreboard anyone can open from a share link.',
          'Scores queue while offline and upload when you are back in range.',
          'Hand the scoring over to someone else mid-match.',
          'A Live now section on everyone\'s home screen.',
        ],
      },
      {
        key: 'match-events',
        icon: 'mdi-bullseye-arrow',
        title: 'Match events',
        blurb: '180s, checkouts, goals and assists per player, with leaderboards.',
        detail: [
          'Per-player events chosen by the sport.',
          'Most 180s this season, top scorer, best checkout. Automatic leaderboards.',
        ],
      },
      {
        key: 'ai-reports',
        icon: 'mdi-text-box-check-outline',
        title: 'AI match reports',
        blurb: 'A written summary of each match. Opt-in per competition.',
        detail: [
          'Opt-in per competition. Never for juniors.',
        ],
      },
    ],
  },
  {
    key: 'stats',
    short: 'Stats',
    icon: 'mdi-chart-box-outline',
    title: 'Stats',
    lead: 'Records and form for every player, club and competition.',
    items: [
      {
        key: 'player-stats',
        demo: 'stats',
        icon: 'mdi-account-star-outline',
        title: 'Player stats',
        blurb: 'Record, streaks, average position, most played, most beaten.',
        detail: [
          'Per club and across all your clubs.',
          'A Tap Leagues score that moves with your results.',
          'A public profile page for every player.',
        ],
      },
      {
        key: 'competition-stats',
        demo: 'stats',
        icon: 'mdi-chart-timeline-variant',
        title: 'Competition stats',
        blurb: 'Form tables and leaderboards for each league, ladder, tournament, americano and meet.',
      },
    ],
  },
  {
    key: 'communication',
    short: 'Communication',
    icon: 'mdi-bullhorn-outline',
    title: 'Notifications, email and chat',
    lead: 'Push and email per notification type, announcements, bulk email, chat.',
    items: [
      {
        key: 'notifications',
        demo: 'notifications',
        icon: 'mdi-bell-outline',
        title: 'Notifications',
        blurb: 'Push and email, switchable per type.',
        detail: [
          'Around 45 kinds, each with its own push and email switch for every member.',
          'Admin alerts for join requests, leavers, placements and results to approve.',
          'Everything shown in the member\'s own timezone.',
        ],
      },
      {
        key: 'announcements',
        icon: 'mdi-bullhorn-outline',
        title: 'Announcements',
        blurb: 'Pinned to every member\'s feed, with an image and a link. Can be scheduled and expire.',
      },
      {
        key: 'email-members',
        icon: 'mdi-email-multiple-outline',
        title: 'Email members',
        blurb: 'The whole club, one competition or one division. Unsubscribes handled.',
        detail: [
          'Filter by club, league, cycle, division, ladder, tournament, meet or americano.',
          'Your club\'s name and footer on every email.',
        ],
      },
      {
        key: 'email-preferences',
        icon: 'mdi-email-check-outline',
        title: 'Email preferences',
        blurb: 'Members manage their own, from any email, without signing in.',
        detail: [
          'One-click unsubscribe that mail apps recognise.',
          'Account, booking and payment emails always arrive.',
        ],
      },
      {
        key: 'chat',
        demo: 'chat',
        icon: 'mdi-chat-outline',
        title: 'Chat',
        blurb: 'A chat for every club, competition, division and challenge, membership kept in sync.',
        detail: [
          'Members are added and removed as the competition changes.',
          'Direct and group chats too, with photos, typing and read receipts.',
          'Mute any chat. Delete your own message for everyone.',
          'Unread messages digested into one email if someone is away from the app.',
        ],
      },
    ],
  },
  {
    key: 'safety',
    short: 'Safety',
    icon: 'mdi-shield-check-outline',
    title: 'Approvals, safety and privacy',
    lead: 'Approvals in one place, privacy for clubs and members, message reporting, blocking, junior protections.',
    items: [
      {
        key: 'approvals',
        icon: 'mdi-check-decagram-outline',
        title: 'Approvals',
        blurb: 'Results, join requests and placements waiting for an admin, in one place.',
      },
      {
        key: 'reports',
        icon: 'mdi-flag-outline',
        title: 'Message reports',
        blurb: 'Members can report a message. Admins see and act on reports.',
        detail: [
          'Reports go to the club\'s admins, or to Tap Leagues where that is right.',
          'A reports queue on the admin dashboard.',
        ],
      },
      {
        key: 'blocking',
        icon: 'mdi-account-cancel-outline',
        title: 'Blocking',
        blurb: 'A blocked member cannot message you.',
      },
      {
        key: 'juniors',
        icon: 'mdi-human-child',
        title: 'Junior protections',
        blurb: 'Under-18s are never public and can\'t be followed. No AI previews involving a junior.',
        detail: [
          'Shown as a private player, or first name and initial, to anyone outside their club.',
          'Left off a public club\'s member list for people outside the club.',
          'Shortened names on printed PDFs unless a parent has given permission.',
          'No AI previews or reports involving a junior.',
        ],
      },
      {
        key: 'privacy',
        icon: 'mdi-eye-off-outline',
        title: 'Club privacy',
        blurb: 'Members only by default, or public. Each competition can choose its own.',
        detail: [
          'Outsiders see the club name, logo and how to join, nothing more.',
          'Choose whether the club appears in Find a club.',
          'Preview your club page exactly as a visitor sees it.',
        ],
      },
      {
        key: 'member-privacy',
        icon: 'mdi-account-lock-outline',
        title: 'Member privacy',
        blurb: 'Each member chooses who outside their clubs can see their profile.',
        detail: [
          'Public, club members only, or hidden as a private player.',
          'Full names and photos stay inside the club unless a member chooses otherwise.',
          'Club admins always see their own members\' details.',
        ],
      },
      {
        key: 'playing-areas',
        icon: 'mdi-map-marker-outline',
        title: 'Playing areas',
        blurb: 'Courts, tables and boards, assignable to matches and challenges.',
      },
    ],
  },
  {
    key: 'showcase',
    short: 'Showcase',
    icon: 'mdi-television-play',
    title: 'Public pages, widgets and TV',
    lead: 'Public pages, website widgets, a clubhouse screen and PDFs.',
    items: [
      {
        key: 'public-pages',
        icon: 'mdi-web',
        title: 'Public pages',
        blurb: 'A page for the club, every competition, every player and every match, public when you choose.',
        detail: [
          'Share any of them and the link shows a proper preview.',
          'Public or members only, for the club and for each competition.',
        ],
      },
      {
        key: 'widgets',
        demo: 'widget',
        icon: 'mdi-widgets-outline',
        title: 'Website widgets',
        blurb: 'Standings, ladders, draws, results and stats embedded on the club website.',
        detail: [
          'Your brand colour, standard or compact.',
          'Locked to the websites you allow.',
          'Updates itself.',
        ],
      },
      {
        key: 'clubhouse-tv',
        icon: 'mdi-television-play',
        title: 'Clubhouse TV',
        blurb: 'A rotating screen with live scores, results, tables and a join QR code.',
        to: '/features/clubhouse-tv',
        detail: [
          'Thirteen panels you can show, hide, pin and order.',
          'Result celebrations and a ticker.',
          'Light or dark, your accent colour.',
          'Any browser on any TV. Nothing to install.',
          'Set up from the website or the app.',
        ],
      },
      {
        key: 'pdf',
        icon: 'mdi-file-pdf-box',
        title: 'PDF export',
        blurb: 'Tables, grids, ladders and brackets as PDFs.',
      },
    ],
  },
  {
    key: 'members',
    short: 'Members',
    icon: 'mdi-cellphone-link',
    title: 'For members',
    lead: 'Web and app, one home screen, sign-in options, friends in other clubs.',
    items: [
      {
        key: 'web-and-app',
        demo: 'homeScreen',
        icon: 'mdi-cellphone-link',
        title: 'Web and app',
        blurb: 'iPhone, Android and any browser, with push notifications on the phone.',
        detail: [
          'Live updates everywhere: a result entered on a phone appears on the web the same second.',
          'The app keeps the feed and chats for reading offline.',
        ],
      },
      {
        key: 'home-screen',
        demo: 'homeScreen',
        icon: 'mdi-home-outline',
        title: 'Home screen',
        blurb: 'What needs attention, live matches, upcoming matches, competitions and the activity feed.',
      },
      {
        key: 'sign-in',
        demo: 'signIn',
        icon: 'mdi-login-variant',
        title: 'Sign in',
        blurb: 'Email, Google, Apple or a passkey. Two accounts can be combined.',
      },
      {
        key: 'two-step',
        icon: 'mdi-shield-key-outline',
        title: 'Two-step sign-in',
        blurb: 'An authenticator app code or a passkey as a second step, with recovery codes.',
      },
      {
        key: 'find-friends',
        icon: 'mdi-account-search-outline',
        title: 'Find friends',
        blurb: 'A user tag for every member, and one search for clubs or people.',
      },
      {
        key: 'following',
        icon: 'mdi-account-multiple-plus-outline',
        title: 'Following',
        blurb: 'Follow friends in other clubs. Both agree, then see each other\'s profile and can message.',
      },
    ],
  },
  {
    key: 'integrations',
    short: 'Integrations',
    icon: 'mdi-connection',
    title: 'Integrations',
    lead: 'Rating services and the tools the club already uses.',
    items: [
      {
        key: 'squash-levels',
        icon: 'mdi-chart-line',
        title: 'Squash Levels',
        blurb: 'Results sent automatically for players who opt in. Levels shown on profiles.',
        detail: [
          'Per competition and per player consent.',
          'Juniors are never sent.',
        ],
      },
      {
        key: 'external-ratings',
        icon: 'mdi-star-half-full',
        title: 'External ratings',
        blurb: 'WTN, DUPR and other rating services, synced from results.',
        tag: 'soon',
      },
      {
        key: 'booking',
        icon: 'mdi-calendar-blank-outline',
        title: 'Booking systems',
        blurb: 'Tap Leagues runs alongside the club\'s existing court booking.',
      },
      {
        key: 'assistant',
        icon: 'mdi-robot-happy-outline',
        title: 'Assistant',
        blurb: 'Answers questions about Tap Leagues for club admins.',
      },
    ],
  },
];

export const FEATURE_FAQS = [
  {
    q: 'What does it cost?',
    a: `Nothing to use. If your club takes payments through Tap Leagues, ${COMMISSION_PERCENT}% commission plus Stripe's fees comes out of each payment. No per-entry fee and no setup fee.`,
  },
  {
    q: 'Are there really no limits?',
    a: 'None. Unlimited members, admins, competitions, divisions, players per division and seasons, on every club.',
  },
  {
    q: 'Which sports?',
    a: 'Squash, padel, tennis, pickleball, badminton, table tennis, racketball, darts, snooker, pool, bowls, football, foosball, fencing, basketball and board games, each with its own scoring. If yours is missing, ask.',
  },
  {
    q: 'Do members need the app?',
    a: 'No. Everything works in a browser. The app adds push notifications, offline chat and the phone calendar, and most members end up using it.',
  },
  {
    q: 'Who holds the money?',
    a: 'Your club does. Payments land in the club\'s own Stripe account, set up from inside Tap Leagues. Tap Leagues never holds your funds.',
  },
  {
    q: 'How long does setup take?',
    a: 'A club and a first competition in about ten minutes. Import your members from a spreadsheet and share a join link, and the rest fills itself in.',
  },
];

export const FEATURE_ITEMS = FEATURE_SECTIONS.flatMap(section =>
    section.items.map(item => ({...item, section: section.key})));

export const findFeature = (key) => FEATURE_ITEMS.find(item => item.key === key) || null;
